/**
 * @file src/store/reducers/selectors/modelSelectors.js
 * @description Redux selectors for reading model configuration (static model settings) and active model type.
 */

const staticModelSettings = require('store/data/model_configs.json');

/**
 * Creates a selector that reads a model setting from `staticModelSettings`.
 * Note: this selector intentionally ignores Redux `state` because the source is static.
 *
 * @param {string} modelType - Key for the model type in `staticModelSettings`.
 * @param {string} settingName - Top-level setting name under the model type.
 * @param {string} [subSettingName] - Optional nested setting name under `settingName`.
 * @returns {(state: any) => any | null} Selector that returns the setting value or `null` if not found/invalid.
 */
export const selectModelSetting =
    (modelType, settingName, subSettingName) =>
    (_state) => {
        if (typeof modelType !== "string" || !modelType.trim()) return null;
        if (typeof settingName !== "string" || !settingName.trim()) return null;

        const modelSettings = staticModelSettings?.[modelType];
        if (!modelSettings || typeof modelSettings !== "object") return null;

        const setting = modelSettings?.[settingName];
        if (setting == null) return null;

        if (subSettingName == null) return setting;

        if (typeof subSettingName !== "string" || !subSettingName.trim()) return null;

        const subSetting = setting?.[subSettingName];
        return subSetting == null ? null : subSetting;
    };

/**
 * Selects the static model settings configuration.
 * Note: this selector intentionally ignores Redux `state` because the source is static.
 *
 * @param {any} _state - Redux state tree (unused).
 * @returns {any} The static model settings object.
 */
export const selectModelSettings = (_state) => staticModelSettings;

/**
 * Selects the currently active model type from Redux state.
 *
 * @param {any} state - Redux state tree.
 * @returns {string | null} Active model type or `null` if unavailable.
 */
export const selectActiveModelType = (state) =>
    state?.inputData?.present?.activeModelType ?? null;


/**
 * Selects the static settings for the currently active model type.
 *
 * Note: `staticModelSettings` is a static JSON config; this selector only uses Redux state
 * to determine which model type to read.
 *
 * @param {any} state - Redux state tree.
 * @returns {object | null} Active model's settings object, or `null` if the active model type is missing/invalid.
 */
export const selectActiveModelSettings = (state) => {
    const activeModelType = state?.inputData?.present?.activeModelType;

    if (typeof activeModelType !== "string" || !activeModelType.trim()) return null;

    const settings = staticModelSettings?.[activeModelType];
    return settings && typeof settings === "object" ? settings : null;
};